"use client";

import Image from "next/image";
import Link from "next/link";
import React from "react";
import { usePathname } from "next/navigation";
import { Award, BookOpen, Headphones, LaptopMinimal, LogIn, Newspaper } from "lucide-react";
import { useI18n } from "../i18n";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuthenticatedUser } from "@/app/hooks/use-authenticated-user";
import { buildAppUrl, type AppConfig } from "../config/get-config";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type HeaderMenuItem = {
  id: "home" | "premium" | "practice" | "mockTest" | "blog" | "contact" | "about" | "login" | "signup";
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: "true" | "false" }>;
  external?: boolean;
  variant?: "default" | "highlight";
};

type HomeHeaderProps = {
  runtimeConfig: AppConfig;
};

export default function HomeHeader({ runtimeConfig }: HomeHeaderProps) {
  const { t, lang, setLanguage } = useI18n();
  const pathname = usePathname();
  const isAuthenticated = useAuthenticatedUser();

  const appLinks = React.useMemo(
    () => ({
      dashboard: buildAppUrl("/", runtimeConfig),
      login: buildAppUrl("/login", runtimeConfig),
      signup: buildAppUrl("/signup", runtimeConfig),
      premium: buildAppUrl("/premium", runtimeConfig),
      contact: buildAppUrl("/contact-us", runtimeConfig),
    }),
    [runtimeConfig]
  );

  const authMenuItems = React.useMemo(
    () =>
      {
        if (isAuthenticated) {
          return [] satisfies HeaderMenuItem[];
        }

        const primaryAuthItem = {
              id: "login" as const,
              label: t("header.login"),
              href: appLinks.login,
              icon: LogIn,
              external: true,
            };

        return [
          primaryAuthItem,
          {
            id: "signup" as const,
            label: t("header.signup"),
            href: appLinks.signup,
            icon: BookOpen,
            variant: "highlight" as const,
            external: true,
          },
        ] satisfies HeaderMenuItem[];
      },
    [appLinks.login, appLinks.signup, isAuthenticated, t]
  );

  const headerMenu = React.useMemo(
    () =>
      {
        return [
          { id: "practice", label: "Practice", href: appLinks.dashboard, icon: Headphones, external: true },
          { id: "mockTest", label: "Mock Test", href: appLinks.dashboard, icon: LaptopMinimal, external: true },
          { id: "premium", label: "Pricing", href: appLinks.premium, icon: Award, external: true },
          { id: "blog", label: "Blog", href: "/blogs", icon: Newspaper },
          ...authMenuItems,
        ] satisfies HeaderMenuItem[];
      },
    [appLinks.dashboard, appLinks.premium, authMenuItems]
  );

  const compactHeaderMenu = React.useMemo(
    () =>
      headerMenu.filter((item) =>
        isAuthenticated
          ? item.id === "practice" || item.id === "mockTest" || item.id === "premium" || item.id === "blog"
          : item.id === "practice" || item.id === "mockTest" || item.id === "premium" || item.id === "blog" || item.id === "login" || item.id === "signup"
      ),
    [headerMenu, isAuthenticated]
  );

  const renderMenu = (menuItems: HeaderMenuItem[]) => (
    <div className="premium-tech-nav flex min-w-0 items-center gap-1 overflow-x-auto rounded-full border border-white/10 bg-white/5 px-1 py-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {menuItems.map((item) => {
        const isActive =
          !item.external && (pathname === item.href || pathname?.startsWith(item.href + "/"));
        const ItemIcon = item.icon;

        const className = cn(
          "inline-flex h-10 shrink-0 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-colors",
          isActive
            ? "bg-secondary text-secondary-foreground shadow-sm"
            : "text-primary-foreground/88 hover:bg-white/8 hover:text-primary-foreground",
          item.variant === "highlight" &&
            "border border-white/10 bg-white/5 text-primary-foreground hover:bg-white/10 hover:text-primary-foreground shadow-none"
        );

        if (item.external) {
          return (
            <a
              key={item.id}
              href={item.href}
              className={className}
            >
              <ItemIcon className="size-5" aria-hidden="true" />
              <span className="whitespace-nowrap">{item.label}</span>
            </a>
          );
        }

        return (
          <Link
            key={item.id}
            href={item.href}
            className={className}
            aria-current={isActive ? "page" : undefined}
          >
            <ItemIcon className="size-5" aria-hidden="true" />
            <span className="whitespace-nowrap">{item.label}</span>
          </Link>
        );
      })}
    </div>
  );

  return (
    <header className="premium-tech-header sticky top-0 z-50 border-b border-white/10 bg-[#031235]/96 text-primary-foreground shadow-[0_1px_0_rgba(255,255,255,0.03)] backdrop-blur-xl">
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div className="premium-tech-header-grid absolute inset-0" />
        <div className="premium-tech-header-squares absolute inset-0" />
      </div>
      <div className="relative z-10 mx-auto flex min-h-14 w-full max-w-[1200px] items-center justify-center gap-4 px-3 py-1 sm:w-[85%] md:justify-between">
        <Link href="/" aria-label="Home" className="relative z-20 flex items-center md:mr-0">
          <Image
            src="/logo/logo-192.webp"
            alt="PTE Ultra Logo"
            width={64}
            height={64}
            className="block h-14 w-auto object-contain sm:h-16"
            priority
            unoptimized
          />
        </Link>
        <a
          href={appLinks.signup}
          className="inline-flex h-10 items-center gap-2 rounded-full border border-white/12 bg-white/10 px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-white/15 md:hidden"
        >
          <BookOpen className="size-4" aria-hidden="true" />
          <span className="whitespace-nowrap">Practice Now</span>
        </a>
        <nav
          aria-label="Primary"
          className="hidden min-w-0 flex-1 items-center justify-center md:flex"
        >
          <div className="hidden lg:flex">{renderMenu(headerMenu)}</div>
          <div className="hidden md:flex lg:hidden">{renderMenu(compactHeaderMenu)}</div>
        </nav>

        <div className="hidden items-center gap-2 sm:gap-3 lg:flex">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="premium-tech-chip border-white/12 bg-white/5 text-primary-foreground shadow-sm hover:bg-white/10"
                aria-label={t("lang.switch")}
                title={t("lang.switch")}
              >
                <span>
                  {lang === "en" ? t("lang.en") : t("lang.ta")}
                </span>
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              <DropdownMenuCheckboxItem
                checked={lang === "en"}
                onSelect={() => setLanguage("en")}
              >
                {t("lang.en")}
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
